import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Sparkles } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SummaryPeriod } from '@/hooks/useMultiPeriodFocusStats';
import { cn } from '@/lib/utils';

interface AISummaryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: string | null;
  isLoading: boolean;
  period: SummaryPeriod | null;
  periodLabel: string;
}

const periodDisplayNames: Record<SummaryPeriod, string> = {
  yesterday: 'Yesterday',
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
};

export function AISummaryDrawer({
  open,
  onOpenChange,
  summary,
  isLoading,
  period,
  periodLabel,
}: AISummaryDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh] focus:outline-none">
        <div className="mx-auto w-full max-w-lg flex flex-col max-h-[calc(85vh-2rem)]">
          <DrawerHeader className="text-center pb-2 flex-shrink-0">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="p-2 rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
            </div>
            <DrawerTitle className="text-xl font-semibold">
              {period ? periodDisplayNames[period] : 'AI Summary'}
            </DrawerTitle>
            <DrawerDescription className="text-muted-foreground">
              {periodLabel}
            </DrawerDescription>
          </DrawerHeader>

          <ScrollArea className="flex-1 overflow-auto">
            <div className="px-0 pb-4">
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-[90%]" />
                  <Skeleton className="h-4 w-[85%]" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-[75%]" />
                </div>
              ) : summary ? (
                <div
                  className={cn(
                    'prose prose-sm dark:prose-invert max-w-none',
                    'prose-p:text-foreground prose-p:leading-relaxed prose-p:my-2',
                    'prose-strong:text-foreground prose-strong:font-semibold',
                    'prose-ul:my-2 prose-li:my-0.5',
                    'prose-headings:text-foreground prose-headings:font-semibold',
                    '[&>ul]:list-none [&>ul]:pl-0',
                    '[&>ul>li]:relative [&>ul>li]:pl-6',
                    '[&>ul>li]:before:content-["•"] [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:text-primary [&>ul>li]:before:font-bold'
                  )}
                >
                  <ReactMarkdown>{summary}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No summary available
                </p>
              )}
            </div>
          </ScrollArea>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
